import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import {
  transitions,
  positions,
  Provider as AlertProvider,
} from "@blaumaus/react-alert";

import {
  CAN_LIST_R365_REPORTS,
  OFFERLIST,
  CAN_USE_VENDOR_OFFERS,
  SHIPMENTREPORT,
} from "./utils/permissons";

import store from "./store";

import { Provider } from "react-redux";

// Layouts
import Public from "./containers/layouts/Public";
import DefaultLayout from "./containers/layouts/DefaultLayout";
import MarketLayout from "./containers/layouts/MarketLayout";
import ProviderLayout from "./containers/layouts/ProviderLayout";
import Login from "./components/layouts/Login";
import PageLoading from "./components/generics/PageLoading";
import PageErrorBoundary from "./components/generics/PageErrorBoundary";
import { routeImports } from "./routing/routeImports";
import { AlertTemplate } from "./utils/Helpers";
import { SUPER_USER, PLANT_USER, MARKET_USER } from "./utils/AuthService";

// Boundaries belong to pages so the active layout and navigation stay visible.
// Keep each lazy component at module scope to preserve its identity on rerenders.
const lazyPage = (loadPage) => {
  const Page = lazy(loadPage);
  const LazyPage = (props) => (
    <PageErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <Page {...props} />
      </Suspense>
    </PageErrorBoundary>
  );
  return LazyPage;
};

// Orders
const OrderListContainer = lazyPage(routeImports.OrderListContainer.load);
const OrderDetailContainer = lazyPage(routeImports.OrderDetailContainer.load);
const HolidayCateringContainer = lazyPage(routeImports.HolidayCateringContainer.load);

// Purchase Orders
const PurchaseOrderListContainer = lazyPage(routeImports.PurchaseOrderListContainer.load);
const PurchaseOrderCreateContainer = lazyPage(routeImports.PurchaseOrderCreateContainer.load);
const PurchaseOrderDetailContainer = lazyPage(routeImports.PurchaseOrderDetailContainer.load);
const PurchaseOrderByPriceDetailContainer = lazyPage(routeImports.PurchaseOrderByPriceDetailContainer.load);
const PurchaseOrderInvoiceDetailContainer = lazyPage(routeImports.PurchaseOrderInvoiceDetailContainer.load);

// Pending Inventory
const PendingInventoryListContainer = lazyPage(routeImports.PendingInventoryListContainer.load);
const PendingInventoryDetailContainer = lazyPage(routeImports.PendingInventoryDetailContainer.load);

// Price Lists
const PriceListsListContainer = lazyPage(routeImports.PriceListsListContainer.load);
const PriceListDetailContainer = lazyPage(routeImports.PriceListDetailContainer.load);
const PriceListsComparison = lazyPage(routeImports.PriceListsComparison.load);
const PriceListErrorsContainer = lazyPage(routeImports.PriceListErrorsContainer.load);
const CorrectionRulesContainer = lazyPage(routeImports.CorrectionRulesContainer.load);

// Invoices
const InvoiceContainer = lazyPage(routeImports.InvoiceContainer.load);
const InvoiceListContainer = lazyPage(routeImports.InvoiceListContainer.load);
const LabelsContainer = lazyPage(routeImports.LabelsContainer.load);
const ReceiveContainer = lazyPage(routeImports.ReceiveContainer.load);

// Inventory
const InventoryDetailContainer = lazyPage(routeImports.InventoryDetailContainer.load);
const InventoryLocationsEditContainer = lazyPage(routeImports.InventoryLocationsEditContainer.load);
const InventoryAuditListContainer = lazyPage(routeImports.InventoryAuditListContainer.load);
const InventoryAuditContainer = lazyPage(routeImports.InventoryAuditContainer.load);
const InventoryAuditViewContainer = lazyPage(routeImports.InventoryAuditViewContainer.load);

// Credit Notes
const CreditNoteContainer = lazyPage(routeImports.CreditNoteContainer.load);
const CreditNoteDetailContainer = lazyPage(routeImports.CreditNoteDetailContainer.load);
const CreditNoteListContainer = lazyPage(routeImports.CreditNoteListContainer.load);

// Sessions
const SessionsListContainer = lazyPage(routeImports.SessionsListContainer.load);
const SessionContainer = lazyPage(routeImports.SessionContainer.load);
const ProductionContainer = lazyPage(routeImports.ProductionContainer.load);
const ProductionIntermediateLabelsContainer = lazyPage(routeImports.ProductionIntermediateLabelsContainer.load);
const ProductionLabelsContainer = lazyPage(routeImports.ProductionLabelsContainer.load);
const ViewProductionContainer = lazyPage(routeImports.ViewProductionContainer.load);
const EditProductionContainer = lazyPage(routeImports.EditProductionContainer.load);

// Shipment
const ShipmentListContainer = lazyPage(routeImports.ShipmentListContainer.load);
const ShipmentNewContainer = lazyPage(routeImports.ShipmentNewContainer.load);
const ShipmentEditContainer = lazyPage(routeImports.ShipmentEditContainer.load);
const ShipmentDetailContainer = lazyPage(routeImports.ShipmentDetailContainer.load);

// Returns
const ReturnListContainer = lazyPage(routeImports.ReturnListContainer.load);
const ReturnContainer = lazyPage(routeImports.ReturnContainer.load);
const ReturnDetailContainer = lazyPage(routeImports.ReturnDetailContainer.load);
const ReturnAddInventoryContainer = lazyPage(routeImports.ReturnAddInventoryContainer.load);

// Market Inventory
const BarcodeScannerContainer = lazyPage(routeImports.BarcodeScannerContainer.load);
const StockLevelsDashboardContainer = lazyPage(routeImports.StockLevelsDashboardContainer.load);
const AlertsPanelContainer = lazyPage(routeImports.AlertsPanelContainer.load);
const ConsumptionHistoryContainer = lazyPage(routeImports.ConsumptionHistoryContainer.load);

// Maintenance
const MaintenanceDashboardContainer = lazyPage(routeImports.MaintenanceDashboardContainer.load);
const ScheduledTasksDashboardContainer = lazyPage(routeImports.ScheduledTasksDashboardContainer.load);
const MachinesListContainer = lazyPage(routeImports.MachinesListContainer.load);
const MachineDetailContainer = lazyPage(routeImports.MachineDetailContainer.load);
const MachineFormContainer = lazyPage(routeImports.MachineFormContainer.load);
const FacilitiesListContainer = lazyPage(routeImports.FacilitiesListContainer.load);
const FacilityDetailContainer = lazyPage(routeImports.FacilityDetailContainer.load);
const FacilityFormContainer = lazyPage(routeImports.FacilityFormContainer.load);
const TaskTemplatesListContainer = lazyPage(routeImports.TaskTemplatesListContainer.load);
const TaskTemplateFormContainer = lazyPage(routeImports.TaskTemplateFormContainer.load);
const TaskAssignmentsListContainer = lazyPage(routeImports.TaskAssignmentsListContainer.load);
const TaskAssignmentFormContainer = lazyPage(routeImports.TaskAssignmentFormContainer.load);
const MaintenanceRecordsListContainer = lazyPage(routeImports.MaintenanceRecordsListContainer.load);
const MaintenanceRecordDetailContainer = lazyPage(routeImports.MaintenanceRecordDetailContainer.load);
const MaintenanceRecordFormContainer = lazyPage(routeImports.MaintenanceRecordFormContainer.load);
const MaintenanceSettingsContainer = lazyPage(routeImports.MaintenanceSettingsContainer.load);

// Reports
const InventoryByItemListContainer = lazyPage(routeImports.InventoryByItemListContainer.load);
const InventoryListContainer = lazyPage(routeImports.InventoryListContainer.load);
const Report_R365 = lazyPage(routeImports.Report_R365.load);
const ReportExpiredContainer = lazyPage(routeImports.ReportExpiredContainer.load);
const ReportIngredientsContainer = lazyPage(routeImports.ReportIngredientsContainer.load);
const ReportInvoicesContainer = lazyPage(routeImports.ReportInvoicesContainer.load);
const ReportProductionContainer = lazyPage(routeImports.ReportProductionContainer.load);
const ReportReceivingContainer = lazyPage(routeImports.ReportReceivingContainer.load);
const ReportShipmentContainer = lazyPage(routeImports.ReportShipmentContainer.load);
const ReportRecallContainer = lazyPage(routeImports.ReportRecallContainer.load);
const ReportWasteContainer = lazyPage(routeImports.ReportWasteContainer.load);
const ReportYieldContainer = lazyPage(routeImports.ReportYieldContainer.load);
const WeightedPriceContainer = lazyPage(routeImports.WeightedPriceContainer.load);

// Provider Portal
const ProviderDashboardContainer = lazyPage(routeImports.ProviderDashboardContainer.load);
const ProviderTaskListContainer = lazyPage(routeImports.ProviderTaskListContainer.load);
const ProviderTaskDetailContainer = lazyPage(routeImports.ProviderTaskDetailContainer.load);

const options = {
  // you can also just use 'bottom center'
  position: positions.TOP_CENTER,
  timeout: 10000,
  offset: "30px",
  // you can also just use 'scale'
  transition: transitions.SCALE,
  containerStyle: {
    marginTop: "30px",
    width: "100%",
    zIndex: 4,
  },
};

const App = (appProps) => (
  <Provider store={store}>
    <AlertProvider template={AlertTemplate} {...options}>
      <Router>
        <div className="app">
          <Routes>
            {/* Orders */}
            <Route element={<MarketLayout />}>
              <Route
                path="/orders"
                element={<OrderListContainer {...appProps} />}
              />
              <Route
                path="/orders/holiday-catering"
                element={<HolidayCateringContainer {...appProps} />}
              />
              <Route
                path="/orders/:id"
                element={<OrderDetailContainer {...appProps} />}
              />
            </Route>
            {/* Purchase Orders (for market and plant users)*/}
            <Route
              element={
                <DefaultLayout
                  permissionsRequired={[
                    { model: OFFERLIST, perms: [CAN_USE_VENDOR_OFFERS] },
                  ]}
                  isFeatureFlag
                  rolesRequired={[PLANT_USER, MARKET_USER]}
                  {...appProps}
                />
              }
            >
              <Route
                path="/purchase_orders_by_price/:id"
                element={<PurchaseOrderByPriceDetailContainer {...appProps} />}
              />
            </Route>
            <Route
              element={
                <DefaultLayout rolesRequired={[PLANT_USER, MARKET_USER]} />
              }
            >
              <Route
                path="/purchase_orders"
                element={<PurchaseOrderListContainer {...appProps} />}
              />
              <Route
                path="/purchase_orders/:id"
                element={<PurchaseOrderDetailContainer {...appProps} />}
              />
            </Route>
            {/* Purchase Orders (only for plant users)*/}
            <Route element={<DefaultLayout />}>
              <Route
                path="/purchase_orders/new"
                element={<PurchaseOrderCreateContainer {...appProps} />}
              />
              <Route
                path="/purchase_orders/:id/invoice_create"
                element={<ReceiveContainer {...appProps} />}
              />
              <Route
                path="/purchase_orders/:id/invoice"
                element={<PurchaseOrderInvoiceDetailContainer {...appProps} />}
              />
              <Route
                path="/pending_inventory"
                element={<PendingInventoryListContainer {...appProps} />}
              />
              <Route
                path="/pending_inventory/:id"
                element={<PendingInventoryDetailContainer {...appProps} />}
              />
            </Route>

            {/* Price Lists */}
            <Route
              element={
                <DefaultLayout
                  permissionsRequired={[
                    { model: OFFERLIST, perms: [CAN_USE_VENDOR_OFFERS] },
                  ]}
                  isFeatureFlag
                  {...appProps}
                />
              }
            >
              <Route
                path="/price_lists"
                element={<PriceListsListContainer {...appProps} />}
              />
              <Route
                path="/price_lists/:id"
                element={<PriceListDetailContainer {...appProps} />}
              />
              <Route
                path="/price_lists/:id/errors"
                element={<PriceListErrorsContainer {...appProps} />}
              />
              <Route
                path="/compare_price_lists"
                element={<PriceListsComparison {...appProps} />}
              />
              <Route
                path="/price_lists/correction-rules"
                element={<CorrectionRulesContainer {...appProps} />}
              />
            </Route>
            {/* Invoices */}
            <Route element={<DefaultLayout />}>
              <Route
                path="/invoice"
                element={<InvoiceListContainer {...appProps} />}
              />
              <Route
                path="/invoice/new"
                element={<ReceiveContainer {...appProps} />}
              />
              <Route
                path="/invoice/:id"
                element={<InvoiceContainer {...appProps} />}
              />
              <Route
                path="/invoice/:id/labels"
                element={<LabelsContainer {...appProps} />}
              />
            </Route>
            {/* Credit Notes */}
            <Route element={<DefaultLayout />}>
              <Route
                path="/credit_note"
                element={<CreditNoteListContainer {...appProps} />}
              />
              <Route
                path="/credit_note/new"
                element={<CreditNoteContainer {...appProps} />}
              />
              <Route
                path="/credit_note/:id"
                element={<CreditNoteDetailContainer {...appProps} />}
              />
            </Route>
            {/* Sessions */}
            <Route element={<DefaultLayout />}>
              <Route
                path="/session"
                element={<SessionsListContainer {...appProps} />}
              />
              <Route
                path="/session/new"
                element={<SessionContainer {...appProps} />}
              />
              <Route
                path="/session/:id"
                element={<ViewProductionContainer {...appProps} />}
              />
              <Route
                path="/session/:id/edit"
                element={<ProductionContainer {...appProps} />}
              />
              <Route
                path="/session/:id/edit_finished"
                element={<EditProductionContainer {...appProps} />}
              />
              <Route
                path="/session/:id/intermediate_labels"
                element={
                  <ProductionIntermediateLabelsContainer {...appProps} />
                }
              />
              <Route
                path="/session/:id/labels"
                element={<ProductionLabelsContainer {...appProps} />}
              />
            </Route>
            {/* Shipments */}

            <Route element={<DefaultLayout />}>
              <Route
                path="/shipment"
                element={<ShipmentListContainer {...appProps} />}
              />
              <Route
                path="/shipment/new"
                element={<ShipmentNewContainer {...appProps} />}
              />
              <Route
                path="/shipment/:id"
                element={<ShipmentDetailContainer {...appProps} />}
              />
              <Route
                path="/shipment/:id/edit"
                element={<ShipmentEditContainer {...appProps} />}
              />
            </Route>
            {/* Market Inventory (for market users) */}
            <Route element={<MarketLayout />}>
              <Route
                path="/market-inventory/scanner"
                element={<BarcodeScannerContainer {...appProps} />}
              />
              <Route
                path="/market-inventory/stock-levels"
                element={<StockLevelsDashboardContainer {...appProps} />}
              />
              <Route
                path="/market-inventory/alerts"
                element={<AlertsPanelContainer {...appProps} />}
              />
              <Route
                path="/market-inventory/consumption"
                element={<ConsumptionHistoryContainer {...appProps} />}
              />
            </Route>
            {/* Maintenance */}
            <Route element={<DefaultLayout />}>
              <Route
                path="/maintenance"
                element={<MaintenanceDashboardContainer {...appProps} />}
              />
              <Route
                path="/maintenance/tasks"
                element={<ScheduledTasksDashboardContainer {...appProps} />}
              />
              <Route
                path="/maintenance/machines"
                element={<MachinesListContainer {...appProps} />}
              />
              <Route
                path="/maintenance/machines/new"
                element={<MachineFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/machines/:id"
                element={<MachineDetailContainer {...appProps} />}
              />
              <Route
                path="/maintenance/machines/:id/edit"
                element={<MachineFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/facilities"
                element={<FacilitiesListContainer {...appProps} />}
              />
              <Route
                path="/maintenance/facilities/new"
                element={<FacilityFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/facilities/:id"
                element={<FacilityDetailContainer {...appProps} />}
              />
              <Route
                path="/maintenance/facilities/:id/edit"
                element={<FacilityFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-templates"
                element={<TaskTemplatesListContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-templates/new"
                element={<TaskTemplateFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-templates/:id"
                element={<TaskTemplateFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-templates/:id/edit"
                element={<TaskTemplateFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-assignments"
                element={<TaskAssignmentsListContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-assignments/new"
                element={<TaskAssignmentFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-assignments/:id/edit"
                element={<TaskAssignmentFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/records"
                element={<MaintenanceRecordsListContainer {...appProps} />}
              />
              <Route
                path="/maintenance/records/new"
                element={<MaintenanceRecordFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/records/service-visit"
                element={<MaintenanceRecordFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/records/:id"
                element={<MaintenanceRecordDetailContainer {...appProps} />}
              />
              <Route
                path="/maintenance/records/:id/edit"
                element={<MaintenanceRecordFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/settings"
                element={<MaintenanceSettingsContainer {...appProps} />}
              />
              <Route
                path="/maintenance/service-providers"
                element={<MaintenanceSettingsContainer {...appProps} />}
              />
            </Route>
            {/* Returns */}

            <Route element={<DefaultLayout />}>
              <Route
                path="/return"
                element={<ReturnListContainer {...appProps} />}
              />
              <Route
                path="/return/new"
                element={<ReturnContainer {...appProps} />}
              />
              <Route
                path="/return/:id"
                element={<ReturnDetailContainer {...appProps} />}
              />
              <Route
                path="/return/:id/add_inventory"
                element={<ReturnAddInventoryContainer {...appProps} />}
              />
            </Route>
            {/* Reports */}
            <Route element={<DefaultLayout rolesRequired={[SUPER_USER]} />}>
              <Route
                path="/reports/inventory"
                element={<InventoryListContainer {...appProps} />}
              />
              <Route
                path="/reports/inventory_items"
                element={<InventoryByItemListContainer {...appProps} />}
              />
              <Route
                path="/reports/shipment"
                element={<ReportShipmentContainer {...appProps} />}
              />
              <Route
                path="/reports/recall"
                element={<ReportRecallContainer {...appProps} />}
              />
              <Route
                path="/reports/invoices"
                element={<ReportInvoicesContainer {...appProps} />}
              />
              <Route
                path="/reports/production"
                element={<ReportProductionContainer {...appProps} />}
              />
              <Route
                path="/reports/waste"
                element={<ReportWasteContainer {...appProps} />}
              />
              <Route
                path="/reports/yield"
                element={<ReportYieldContainer {...appProps} />}
              />
              <Route
                path="/reports/expired_inventories"
                element={<ReportExpiredContainer {...appProps} />}
              />
              <Route
                path="/reports/ingredients"
                element={<ReportIngredientsContainer {...appProps} />}
              />
              <Route
                path="/reports/weighted_price"
                element={<WeightedPriceContainer {...appProps} />}
              />
              <Route
                path="/reports/receiving"
                element={<ReportReceivingContainer {...appProps} />}
              />
            </Route>
            <Route
              element={
                <DefaultLayout
                  rolesRequired={[SUPER_USER]}
                  permissionsRequired={[
                    { model: SHIPMENTREPORT, perms: [CAN_LIST_R365_REPORTS] },
                  ]}
                />
              }
            >
              <Route
                path="/reports/R365"
                element={<Report_R365 {...appProps} />}
              />
            </Route>
            {/* Inventories */}
            <Route element={<DefaultLayout />}>
              <Route
                path="/inventory/audit"
                element={<InventoryAuditListContainer {...appProps} />}
              />
              <Route
                path="/inventory/locations"
                element={<InventoryLocationsEditContainer {...appProps} />}
              />
              <Route
                path="/inventory/:id"
                element={<InventoryDetailContainer {...appProps} />}
              />
              <Route
                path="/inventory/audit/:id/edit"
                element={<InventoryAuditContainer {...appProps} />}
              />
              <Route
                path="/inventory/audit/:id"
                element={<InventoryAuditViewContainer {...appProps} />}
              />
            </Route>
            {/* Provider Portal */}
            <Route element={<ProviderLayout />}>
              <Route
                path="/provider-portal"
                element={<ProviderDashboardContainer {...appProps} />}
              />
              <Route
                path="/provider-portal/tasks"
                element={<ProviderTaskListContainer {...appProps} />}
              />
              <Route
                path="/provider-portal/tasks/:id"
                element={<ProviderTaskDetailContainer {...appProps} />}
              />
            </Route>
            {/* Login */}
            <Route
              path="/login"
              element={<Public component={Login} {...appProps} />}
            />
            {/* Default */}
            <Route path="*" element={<Navigate to="/orders" />} />
          </Routes>
        </div>
      </Router>
    </AlertProvider>
  </Provider>
);

export default App;
